import { ExpandMore } from "@material-ui/icons";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CircularProgress, Fade } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import LineChart from "../../../components/Charts/LineChart";
import TextView from "../../../components/TextView";
import { getReferralsChart } from "../../../services/api";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";

const Wrapper = styled.div`
  background-color: #fffaf0;
  border-radius: 10px;
  padding: 20px 14px 0px 14px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  .recharts-responsive-container {
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    min-height: 300px;
  }

  .recharts-wrapper {
    width: 100% !important;
    max-width: 100%;
    height: auto !important;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const HeaderIcon = styled.img`
  width: 36px;
  height: 36px;
  margin-right: 10px;
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  border-radius: 5px;
  padding: 8px;
  margin-top: 20px;
  background: #fcf1dc;
  border: 0.5px solid #d8bf8f;
`;

const CustomSelect = styled.div`
  cursor: pointer;
  display: flex;
`;

const CustomMenuItem = styled(MenuItem)`
  .MuiListItem-root {
    padding: 8px;
  }
`;

const CustomDropdown = styled.div`
  position: absolute;
  top: 30px;
  background: #fcf1dc;
  border-radius: 5px;
  width: 80px;
  z-index: 12;
  max-height: 160px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e6cfa2;

    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0ae6a;

    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #d0ae6a;
  }
`;

const Outer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 10;
`;

const Loader = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const chartTypes = {
  revenue: {
    value: "Revenue",
    icon: ASSETS.revenueIcon,
  },
  referrals: {
    value: "Referrals",
    icon: ASSETS.referralsIcon,
  },
  // unlocks: {
  //   value: "Unlocks",
  //   icon: ASSETS.unlockIcon,
  // },
};

const months = {
  0: "Annual",
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const emptyChartData = {
  total: 0,
  x_axis: [],
  y_axis: [],
  y1_axis: [],
};

export default function Chart() {
  const [chartType, setChartType] = useState("revenue");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [valueLoading, setValueLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [chartData, setChartData] = useState(emptyChartData);

  useEffect(() => {
    getChartData();
  }, [selectedYear, selectedMonth, chartType]);

  useEffect(() => {
    generateYearOptions();
  }, []);

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 10; i++) {
      years.push(currentYear - i);
    }
    setYears(years);
  };

  const onHideThings = () => {
    setShowYearDropdown(false);
    setShowMonthDropdown(false);
    setShowDropdown(false);
  };

  const onChangeHandler = (type, option) => {
    if (type === "year") {
      setSelectedYear(option);
      setShowYearDropdown(false);
    } else {
      setSelectedMonth(option);
      setShowMonthDropdown(false);
    }
  };

  const onSelect = (type) => {
    setValueLoading(true);
    setChartType(type);
    setShowDropdown(false);
  };

  const getChartData = async () => {
    setLoading(true);
    setValueLoading(true);
    const timeFrame =
      selectedMonth != 0
        ? `${selectedYear}-${
            selectedMonth < 10 ? 0 + selectedMonth : selectedMonth
          }`
        : `${selectedYear}`;

    try {
      const response = await getReferralsChart(chartType, timeFrame);
      if (response.data.success) {
        setChartData(response.data.data);
      }
    } catch (err) {
      setChartData(emptyChartData);
      console.log(err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setValueLoading(false);
      }, 300); // Short delay to ensure smooth transition
    }
  };

  return (
    <Wrapper>
      <Row>
        <HeaderRow>
          <HeaderIcon src={chartTypes[chartType].icon} />
          <div>
            <Row
              style={{ cursor: "pointer" }}
              onClick={() => setShowDropdown(true)}
            >
              <TextView variant="subtitle">
                {chartTypes[chartType].value}
              </TextView>{" "}
              <ExpandMore />
            </Row>
            <div
              style={{
                minHeight: "28px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {valueLoading ? (
                <CircularProgress size={20} style={{ color: colors.primary }} />
              ) : (
                <Fade in={!valueLoading} timeout={300}>
                  <div>
                    {!!chartData && (
                      <TextView variant="title">
                        {chartType == "revenue"
                          ? `$${(chartData?.total / 100).toFixed(2)}`
                          : chartData.total}
                      </TextView>
                    )}
                  </div>
                </Fade>
              )}
            </div>

            {showDropdown && (
              <CustomDropdown style={{ width: 120 }}>
                {Object.keys(chartTypes)
                  .filter((key) => key !== chartType)
                  .map((item) => (
                    <CustomMenuItem
                      onClick={() => onSelect(item)}
                      key={item}
                      value={item}
                    >
                      {chartTypes[item].value}
                    </CustomMenuItem>
                  ))}
              </CustomDropdown>
            )}
          </div>
        </HeaderRow>

        <SelectWrapper>
          <div style={{ position: "relative" }}>
            <CustomSelect onClick={() => setShowYearDropdown(true)}>
              {selectedYear ? selectedYear : "Year"}
              <KeyboardArrowDownIcon />
            </CustomSelect>
            {showYearDropdown && (
              <CustomDropdown>
                {years.map((option) => (
                  <CustomMenuItem
                    onClick={() => onChangeHandler("year", option)}
                    key={option}
                  >
                    {option}
                  </CustomMenuItem>
                ))}
              </CustomDropdown>
            )}
          </div>
          <div style={{ position: "relative" }}>
            <CustomSelect
              style={{ marginLeft: 10 }}
              onClick={() => setShowMonthDropdown(true)}
            >
              {months[selectedMonth]}

              <KeyboardArrowDownIcon />
            </CustomSelect>
            {showMonthDropdown && (
              <CustomDropdown>
                {Object.keys(months).map((key) => (
                  <CustomMenuItem
                    onClick={() => onChangeHandler("month", key)}
                    key={key}
                  >
                    {months[key]}
                  </CustomMenuItem>
                ))}
              </CustomDropdown>
            )}
          </div>
        </SelectWrapper>
      </Row>
      {(showMonthDropdown || showYearDropdown || showDropdown) && (
        <Outer onClick={onHideThings} />
      )}

      {loading ? (
        <Loader>
          <CircularProgress size={32} style={{ color: colors.primary }} />
        </Loader>
      ) : chartType === "revenue" ? (
        <LineChart
          type={chartType}
          data={chartData}
          selectedMetrics={[
            {
              metricType: "revenue",
              backgroundColor: "#F79A0033",
              borderColor: "#FFBC02",
              fill: true,
            },
          ]}
        />
      ) : (
        <LineChart
          type={chartType}
          data={chartData}
          selectedMetrics={[
            {
              metricType: "referrals",
              backgroundColor: "#6366F1",
              borderColor: "#6366F1",
              fill: true,
            },
            {
              fill: false,
              metricType: "referrals",
              backgroundColor: "#E25297",
              borderColor: "#E25297",
            },
          ]}
        />
      )}
    </Wrapper>
  );
}
